import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type InvoiceLine = {
  id?: number
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  revenue_account: number | null
  sort_order: number
  subtotal?: string
  tax_amount?: string
  line_total?: string
}

type Invoice = {
  id: number
  number: string
  partner: number
  partner_name?: string
  invoice_date: string
  due_date: string | null
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID'
  currency_code: string
  notes: string
  subtotal: string
  tax_total: string
  total: string
  lines: InvoiceLine[]
  journal_entry: number | null
  journal_entry_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }

const STATUS_COLOR: Record<Invoice['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-blue-100 text-blue-800',
  PAID: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-700',
}

type TabKey = 'details' | 'lines' | 'files'

// ─────────────────── Page ───────────────────
export default function FinanceInvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const invoiceId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const invoiceQ = useQuery({
    queryKey: ['finance', 'invoice', invoiceId],
    queryFn: async () => (await api.get<Invoice>(`/finance/invoices/${invoiceId}/`)).data,
    enabled: !!invoiceId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Invoice>) =>
      (await api.patch(`/finance/invoices/${invoiceId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'invoice', invoiceId] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/finance/invoices/${invoiceId}/`),
    onSuccess: () => {
      toast.success('Invoice deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      navigate('/admin/finance/invoices')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const postMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/finance/invoices/${invoiceId}/post_to_gl/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'invoice', invoiceId] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
      toast.success('Posted to General Ledger')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (invoiceQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (invoiceQ.isError || !invoiceQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Invoice not found.</p>
          <Link to="/admin/finance/invoices" className="text-blue-600 hover:underline">← Back to invoices</Link>
        </div>
      </AdminLayout>
    )
  }

  const invoice = invoiceQ.data
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const revenueAccounts = (accountsQ.data || []).filter((a) => a.is_active && a.account_type === 'REVENUE')
  const locked = invoice.journal_entry != null

  const save = (payload: Partial<Invoice>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/finance/invoices" className="text-muted-foreground hover:text-foreground hover:underline">← Invoices</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{invoice.number}</div>
            <h2 className="text-2xl font-bold text-foreground">
              {invoice.partner_name || <span className="italic">No customer</span>}
            </h2>
            <div className="text-sm text-muted-foreground">
              <span>Issued {invoice.invoice_date}</span>
              {invoice.due_date && <span> · Due {invoice.due_date}</span>}
              <span> · </span>
              <span className="font-mono">{invoice.currency_code} {Number(invoice.total).toFixed(2)}</span>
              {invoice.journal_entry_number && (
                <span> · Posted as <span className="font-mono">{invoice.journal_entry_number}</span></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[invoice.status]}`}>{invoice.status}</span>
            ) : (
              <InlineStatus
                value={invoice.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'OPEN', label: 'Open' },
                  { value: 'PAID', label: 'Paid' },
                  { value: 'VOID', label: 'Void' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {invoice.status === 'DRAFT' && !locked && (
                  <DropdownMenuItem
                    onClick={() => confirm(
                      `Post invoice ${invoice.number} to General Ledger (creates a journal entry and locks line items)`,
                      () => postMut.mutate(),
                    )}
                    disabled={postMut.isPending}
                    className="text-green-700"
                  >
                    Post to General Ledger
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={locked}
                  onClick={() => !locked && confirm(
                    `invoice ${invoice.number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!locked ? 'text-destructive' : ''}
                >
                  {locked ? 'Delete (posted to GL)' : 'Delete invoice'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'lines', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'lines' && `Line items (${invoice.lines.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          invoice={invoice}
          partners={partners}
          locked={locked}
          onSave={save}
        />
      )}
      {tab === 'lines' && (
        <LinesTab
          invoice={invoice}
          revenueAccounts={revenueAccounts}
          locked={locked}
          onSaveLines={(newLines) => save({ lines: newLines } as Partial<Invoice>)}
        />
      )}
      {tab === 'files' && <FilesTab parent="finance.invoice" parentId={invoiceId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  invoice, partners, locked, onSave,
}: {
  invoice: Invoice
  partners: Partner[]
  locked: boolean
  onSave: (payload: Partial<Invoice>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This invoice is <strong>posted to the General Ledger</strong> ({invoice.journal_entry_number}) and read-only.
          Reverse the journal entry to edit, or VOID and reissue.
        </div>
      )}

      <Section title="Invoice header">
        <Row label="Number">
          <InlineText
            value={invoice.number}
            onSave={(v) => onSave({ number: v })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Customer">
          <InlineSelect
            value={String(invoice.partner)}
            displayLabel={invoice.partner_name || <Dash />}
            options={partners.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            disabled={locked}
          />
        </Row>
        <Row label="Invoice date">
          <InlineDate
            value={invoice.invoice_date}
            onSave={(v) => onSave({ invoice_date: v || invoice.invoice_date })}
          />
        </Row>
        <Row label="Due date">
          <InlineDate value={invoice.due_date} onSave={(v) => onSave({ due_date: v })} />
        </Row>
        <Row label="Currency">
          <InlineText
            value={invoice.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Status">
          {locked ? (
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[invoice.status]}`}>{invoice.status}</span>
          ) : (
            <InlineStatus
              value={invoice.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'OPEN', label: 'Open' },
                { value: 'PAID', label: 'Paid' },
                { value: 'VOID', label: 'Void' },
              ]}
              onSave={(v) => onSave({ status: v })}
            />
          )}
        </Row>
      </Section>

      <Section title="Totals">
        <Row label="Subtotal">
          <span className="font-mono">{Number(invoice.subtotal).toFixed(2)} {invoice.currency_code}</span>
        </Row>
        <Row label="Tax">
          <span className="font-mono">{Number(invoice.tax_total).toFixed(2)} {invoice.currency_code}</span>
        </Row>
        <Row label="Total">
          <span className="font-mono font-bold text-base">
            {Number(invoice.total).toFixed(2)} {invoice.currency_code}
          </span>
        </Row>
        {invoice.journal_entry_number && (
          <Row label="GL entry">
            <Link to="/admin/finance/journal-entries"
              className="text-blue-600 hover:underline font-mono text-sm">
              {invoice.journal_entry_number}
            </Link>
          </Row>
        )}
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={invoice.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            disabled={locked}
            placeholder="Customer-visible notes on this invoice"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Line items tab ───────────────────
// Lines are nested under the invoice in the backend — every line edit/add/
// remove triggers a full PATCH that sends the whole lines array. The
// backend recomputes subtotal/tax_total/total server-side.

type LineDraft = {
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  revenue_account: number | null
}

function emptyLineDraft(revenueAccounts: Account[]): LineDraft {
  return {
    description: '',
    quantity: '1',
    unit_price: '0',
    tax_rate: '0',
    revenue_account: revenueAccounts[0]?.id ?? null,
  }
}

function LinesTab({
  invoice, revenueAccounts, locked, onSaveLines,
}: {
  invoice: Invoice
  revenueAccounts: Account[]
  locked: boolean
  onSaveLines: (lines: InvoiceLine[]) => Promise<void>
}) {
  const { confirm, dialog } = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<LineDraft>(emptyLineDraft(revenueAccounts))
  const [pending, setPending] = useState(false)

  function openCreate() {
    setDraft(emptyLineDraft(revenueAccounts))
    setCreating(true)
  }

  async function saveLineField(idx: number, patch: Partial<InvoiceLine>) {
    const next = invoice.lines.map((l, i) => i === idx ? { ...l, ...patch } : l)
    await onSaveLines(serializeLines(next))
  }

  async function addLine() {
    setPending(true)
    try {
      const next: InvoiceLine[] = [
        ...invoice.lines,
        {
          description: draft.description,
          quantity: draft.quantity,
          unit_price: draft.unit_price,
          tax_rate: draft.tax_rate,
          revenue_account: draft.revenue_account,
          sort_order: invoice.lines.length,
        },
      ]
      await onSaveLines(serializeLines(next))
      toast.success('Line added')
      setCreating(false)
    } catch (e) {
      toast.error(extractErr(e))
    } finally {
      setPending(false)
    }
  }

  async function removeLine(idx: number) {
    const next = invoice.lines.filter((_, i) => i !== idx)
    try {
      await onSaveLines(serializeLines(next))
      toast.success('Line removed')
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  // Compute live total for the table footer.
  let subtotal = 0, taxTotal = 0
  invoice.lines.forEach((l) => {
    const s = Number(l.quantity) * Number(l.unit_price)
    const t = s * Number(l.tax_rate) / 100
    subtotal += s; taxTotal += t
  })

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          {locked
            ? 'Invoice posted — line items are read-only. Reverse the journal entry to edit.'
            : 'Edit quantities, unit prices, and tax rates inline. Totals recompute on save.'}
        </p>
        <Button size="sm" onClick={openCreate} disabled={locked}>
          <Plus className="h-3 w-3 mr-1" /> Add line
        </Button>
      </div>

      {invoice.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">No line items yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Description</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit price</Th>
                <Th className="text-right">Tax %</Th>
                <Th>Revenue account</Th>
                <Th className="text-right">Line total</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoice.lines.map((l, i) => {
                const s = Number(l.quantity) * Number(l.unit_price)
                const t = s * Number(l.tax_rate) / 100
                return (
                  <tr key={l.id ?? i}>
                    <Td className="font-medium">
                      <InlineText
                        value={l.description}
                        onSave={(v) => saveLineField(i, { description: v })}
                        disabled={locked}
                        placeholder="Item description"
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.quantity}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { quantity: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.unit_price}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { unit_price: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.tax_rate}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { tax_rate: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}%</span>}
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.revenue_account ? String(l.revenue_account) : ''}
                        displayLabel={l.revenue_account
                          ? (revenueAccounts.find((a) => a.id === l.revenue_account)
                              ? `${revenueAccounts.find((a) => a.id === l.revenue_account)!.code} ${revenueAccounts.find((a) => a.id === l.revenue_account)!.name}`
                              : <Dash />)
                          : <Dash />}
                        allowClear
                        options={revenueAccounts.map((a) => ({
                          value: String(a.id),
                          label: `${a.code} ${a.name}`,
                          searchText: `${a.code} ${a.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { revenue_account: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {(s + t).toFixed(2)}
                    </Td>
                    <Td className="text-right">
                      {!locked && (
                        <RowActions
                          onEdit={() => {/* inline edits cover the whole row */}}
                          onDelete={() => confirm(
                            `line "${l.description || '(blank)'}"`,
                            () => removeLine(i),
                          )}
                        />
                      )}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-muted/40 text-sm">
              <tr>
                <td colSpan={5} className="px-4 py-2 text-right font-medium text-muted-foreground">Subtotal</td>
                <td className="px-4 py-2 text-right font-mono">{subtotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={5} className="px-4 py-2 text-right font-medium text-muted-foreground">Tax</td>
                <td className="px-4 py-2 text-right font-mono">{taxTotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr className="font-bold">
                <td colSpan={5} className="px-4 py-2 text-right text-foreground">Total</td>
                <td className="px-4 py-2 text-right font-mono">{invoice.currency_code} {(subtotal + taxTotal).toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add line item"
        description="Add a billable line to this invoice."
        onSubmit={addLine}
        isPending={pending}
        submitLabel="Add line"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="li-desc">Description *</FieldLabel>
            <Textarea id="li-desc" autoFocus required value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={2} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="li-qty">Quantity</FieldLabel>
              <Input id="li-qty" type="number" step="0.01" value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-price">Unit price</FieldLabel>
              <Input id="li-price" type="number" step="0.01" value={draft.unit_price}
                onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-tax">Tax %</FieldLabel>
              <Input id="li-tax" type="number" step="0.01" value={draft.tax_rate}
                onChange={(e) => setDraft({ ...draft, tax_rate: e.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="li-acct">Revenue account</FieldLabel>
            <SearchableSelect
              id="li-acct"
              value={draft.revenue_account ? String(draft.revenue_account) : NONE}
              onChange={(v) => setDraft({ ...draft, revenue_account: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...revenueAccounts.map((a) => ({
                  value: String(a.id), label: a.name, hint: a.code,
                })),
              ]}
            />
            <FieldDescription>Drives the credit account on the GL posting.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// Strip computed line fields (subtotal/tax_amount/line_total) the backend
// rejects, re-stamp sort_order, and return a clean payload-ready array.
function serializeLines(lines: InvoiceLine[]): InvoiceLine[] {
  return lines.map((l, i) => ({
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
    tax_rate: l.tax_rate,
    revenue_account: l.revenue_account,
    sort_order: i,
  }))
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
